import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import API from 'core/Api';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { usePermissionsContext } from 'store/permissions';
import { FaTimes, FaCheck } from 'react-icons/fa';
import { useWizardContext } from 'store/wizard';
import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';
import { ISelectOptionProps } from 'components/form/types';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import { Badge, Tooltip, OverlayTrigger } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import Button from 'components/Button';
import Wizard from '../../components/Wizard';
import WizardNavAndSubmit from '../../components/WizardNavAndSubmit';
import { docTypeOptions } from '../../wizardItems';
import buildWizardProps from '../../helpers';
import { IInputs } from './types';

interface IProps {
  data?: any;
  isNewWizard?: boolean;
}

interface IDataType {
  [key: string]: string | number | boolean;
}

interface IUserSelectOptionProps extends ISelectOptionProps {
  groups: number[];
  label_en: string;
  status: string;
}

interface ITemplateSelectOptionProps extends ISelectOptionProps {
  bulk_create: boolean;
  label_en: string;
}

interface IGroup {
  label: string;
  value: number | string | boolean;
  selected: boolean;
}

interface IMassDocSelectionsStorage {
  group_values: Array<number | string | boolean>;
  user_ids: number[];
  excluded_user_ids: number[];
}

const StepOne: FC<IProps> = ({ data, isNewWizard = false }: IProps) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const history = useHistory();
  const { showModal } = useModal();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();
  const { currentStep, setCurrentStep, totalSteps, setTotalSteps } = useWizardContext();
  const massButtonRowRef = useRef<HTMLDivElement>(null);
  const location = useLocation<{ employeeId: number }>();

  const isEnglish = language === 'en';

  const queryClient = useQueryClient();

  const hasCanSelectEmployeePermission = checkPermission(PermissionKeys.CAN_SELECT_DOC_WIZARD_EMPLOYEE);

  const hasCanCreateMassDocPermission = checkPermission(PermissionKeys.CAN_CREATE_MASS_DOCUMENTS);

  const { categories, templates, groups, users, type, is_multi_user: isMultiUser, types } = data;
  const massDocStorageKey = `docWizardMassDoc-${client?.id || 'default'}`;
  const massDocSelectionsStorageKey = `docWizardMassSelections-${client?.id || 'default'}`;

  const getStoredMassSelections = (): IMassDocSelectionsStorage | null => {
    if (isNewWizard) {
      return null;
    }

    const storedSelections = localStorage.getItem(massDocSelectionsStorageKey);
    if (!storedSelections) {
      return null;
    }

    try {
      const parsedSelections = JSON.parse(storedSelections);
      const groupValues = Array.isArray(parsedSelections?.group_values) ? parsedSelections.group_values : [];
      const userIds = Array.isArray(parsedSelections?.user_ids)
        ? parsedSelections.user_ids.map((id: unknown) => Number(id)).filter((id: number) => !Number.isNaN(id))
        : [];
      const excludedUserIds = Array.isArray(parsedSelections?.excluded_user_ids)
        ? parsedSelections.excluded_user_ids.map((id: unknown) => Number(id)).filter((id: number) => !Number.isNaN(id))
        : [];

      return {
        group_values: groupValues,
        user_ids: userIds,
        excluded_user_ids: excludedUserIds,
      };
    } catch (error) {
      localStorage.removeItem(massDocSelectionsStorageKey);
      return null;
    }
  };

  const [employees, setEmployees] = useState<Array<IUserSelectOptionProps>>([]);
  const employeesRef = useRef<Array<IUserSelectOptionProps>>([]);
  const [excludedEmployees, setExcludedEmployees] = useState<number[]>(() => {
    return getStoredMassSelections()?.excluded_user_ids || [];
  });

  const defaultCat = categories.find((cat: ISelectOptionProps) => cat.selected);

  const availableTypes = types || docTypeOptions;
  const defaultType =
    availableTypes.find((typeOpt: ISelectOptionProps) => typeOpt.selected) ||
    availableTypes.find((typeOpt: ISelectOptionProps) => typeOpt.value === 'internal') ||
    availableTypes.find((typeOpt: ISelectOptionProps) => typeOpt.value === type) ||
    availableTypes[0];

  const defaultUser = users.find((user: IDataType) => user.value === location.state?.employeeId);

  const buildDefaultValues = () => {
    const persistedMassDoc = !isNewWizard ? localStorage.getItem(massDocStorageKey) : null;
    const defaultIsMassDoc = persistedMassDoc === null ? isMultiUser : persistedMassDoc === 'true';
    const persistedMassSelections = getStoredMassSelections();

    const obj: any = {
      is_multi_user: defaultIsMassDoc,
      type: defaultType,
    };

    if (obj.type) {
      obj.type = {
        ...obj.type,
        label: types ? obj.type.label : t(obj.type.label),
      };
    }

    obj.category_id = defaultCat;
    if (defaultCat) obj.template_id = templates[defaultCat.value].find((temp: ISelectOptionProps) => temp.selected);

    if (hasCanSelectEmployeePermission) {
      if (defaultUser) {
        obj.user_id = defaultUser;
      } else {
        obj.user_id = users.find((user: ISelectOptionProps) => user.selected);
      }
    }

    if (defaultIsMassDoc) {
      const storedGroups = persistedMassSelections?.group_values;
      obj.groups = storedGroups
        ? groups.filter((gr: ISelectOptionProps) => storedGroups.includes(gr.value))
        : groups.filter((gr: ISelectOptionProps) => gr.selected);
    }

    return obj;
  };

  const toggleExclusion = (userId: number) => {
    if (excludedEmployees.includes(userId)) {
      setExcludedEmployees(prev => prev.filter(id => id !== userId));
    } else {
      setExcludedEmployees(prev => [...prev, userId]);
    }
  };

  const methods = useForm<IInputs>({
    defaultValues: buildDefaultValues(),
  });
  const { formState, watch, setError, setValue, reset, unregister, clearErrors, getValues } = methods;
  const { isSubmitting } = formState;

  useEffect(() => {
    const persistedMassSelections = getStoredMassSelections();
    setExcludedEmployees(persistedMassSelections?.excluded_user_ids || []);
    reset(buildDefaultValues());
  }, [data]);

  useEffect(() => {
    if (isNewWizard) {
      localStorage.removeItem(massDocStorageKey);
      localStorage.removeItem(massDocSelectionsStorageKey);
      setExcludedEmployees([]);
    }
  }, [isNewWizard, massDocSelectionsStorageKey, massDocStorageKey]);

  // Watch fields values
  const isMassDoc = watch('is_multi_user');
  const typeField = watch('type');
  const category = watch('category_id');
  const userField = watch('users');
  const groupsFieldValue = watch('groups');
  const groupField = useRef<Array<IGroup>>([]);
  const prevSingleUserRef = useRef<ISelectOptionProps | null>(null);

  useEffect(() => {
    localStorage.setItem(massDocStorageKey, String(!!isMassDoc));
  }, [isMassDoc, massDocStorageKey]);

  useEffect(() => {
    if (!isMassDoc) {
      return;
    }

    const selectedGroupValues = Array.isArray(groupsFieldValue) ? groupsFieldValue.map(gr => gr.value) : [];
    const selectedUserIds = Array.isArray(userField)
      ? userField.map(user => Number(user.value)).filter((id: number) => !Number.isNaN(id))
      : [];

    localStorage.setItem(
      massDocSelectionsStorageKey,
      JSON.stringify({
        group_values: selectedGroupValues,
        user_ids: selectedUserIds,
        excluded_user_ids: excludedEmployees,
      })
    );
  }, [excludedEmployees, groupsFieldValue, isMassDoc, massDocSelectionsStorageKey, userField]);

  useEffect(() => {
    if (isMassDoc) {
      prevSingleUserRef.current = getValues('user_id') || null;
      unregister('user_id');
      clearErrors('user_id');
    } else {
      unregister('users');
      unregister('groups');
      clearErrors(['users', 'groups']);

      if (prevSingleUserRef.current) {
        setValue('user_id', prevSingleUserRef.current);
      }
    }
  }, [clearErrors, getValues, isMassDoc, setValue, unregister]);

  useEffect(() => {
    if (!isMassDoc || !employees.length) {
      return;
    }

    const filteredEmployeesByDeselect = employees.filter(
      (employee: IUserSelectOptionProps) => !excludedEmployees.includes(Number(employee.value))
    );

    setValue('users', filteredEmployeesByDeselect ?? null);
  }, [employees, excludedEmployees, isMassDoc, setValue]);

  const handleGroupChange = useCallback(() => {
    const groupIds = groupField.current?.map((gr: ISelectOptionProps) => gr.value);
    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds?.length) {
      const filteredEmployees = users.filter((empl: IUserSelectOptionProps) => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag: boolean, currGr: number) => {
          return flag || groupIds.includes(currGr);
        }, false);
        return isInSelectedGroups;
      });

      setEmployees(() => {
        const filteredEmployeesMap = filteredEmployees.map((emp: ISelectOptionProps) => ({
          ...emp,
          isDisabled: true,
        }));
        employeesRef.current = filteredEmployeesMap;
        return filteredEmployeesMap;
      });
      setValue('users', filteredEmployees);
    } else {
      if (isMassDoc) {
        setEmployees([]);
        setValue('users', []);
        employeesRef.current = [];
        return;
      }

      setEmployees(() => users);
      employeesRef.current = users;
      setValue('users', users);
    }
  }, [isMassDoc, setValue, users, groupField]);

  useEffect(() => {
    if (groupsFieldValue?.length !== groupField.current?.length) {
      setHasSelectedActive(false);
    }
    if ((!groupsFieldValue?.length || groupsFieldValue.length === 0) && groupField.current?.length) {
      setExcludedEmployees([]);
    }
    groupField.current = groupsFieldValue;
    handleGroupChange();
  }, [handleGroupChange, groupsFieldValue]);

  const generateLabelEn = (emp: IUserSelectOptionProps) => {
    if (isEnglish) {
      return emp.label;
    }

    const trimmedLabel = emp.label_en.split(' - ')[0].trim();

    if (trimmedLabel.length === 0) {
      return null;
    }

    return trimmedLabel;
  };

  const generateLabel = (emp: IUserSelectOptionProps) => {
    let label = '';

    if (isEnglish) {
      label =
        emp.label_en.split(' - ')[0].trim().length !== 0
          ? `${emp.label_en.split(' - ')[0].trim()} - ${emp.label_en.split(' - ')[1].trim()}`
          : `${emp.label.split(' - ')[0].trim()} - ${emp.label_en.split(' - ')[1].trim()}`;
    } else {
      label =
        emp.label_en.split(' - ')[0].trim().length !== 0
          ? `${emp.label.split(' - ')[0].trim()} - ${emp.label.split(' - ')[1].trim()}`
          : emp.label;
    }

    return label;
  };

  const onSubmit = (submitData: IInputs) => {
    localStorage.removeItem('formValues');

    const formattedData = {
      is_multi_user: isMassDoc,
      type: submitData.type.value,
      category_id: submitData.category_id.value,
      ...(submitData.type.value === 'internal' && {
        client_template_id: submitData.template_id.value,
      }),
      ...(hasCanSelectEmployeePermission && {
        user_id: !isMassDoc ? submitData.user_id.value : null,
        groups: isMassDoc ? submitData.groups.map((gr: ISelectOptionProps) => gr.value) : [],
        excluded_user_ids: excludedEmployees,
      }),
    };

    return API.post('document-wizard/step-one', formattedData)
      .then((res: any) => {
        const { total_steps: totalStepsResp, next_step: nextStep } = res.data;
        // Set total steps number into wizard context
        setTotalSteps(totalStepsResp);
        // Set next current step into wizard context
        setCurrentStep(nextStep);
        // Push to next step
        const url = buildClientUrl(`/documents-wizard/step-${nextStep}`, client);
        queryClient.removeQueries(buildClientEntity('step-one', client));
        queryClient.removeQueries(buildClientEntity('step-two', client));
        queryClient.removeQueries(buildClientEntity('step-three', client));
        history.push(url);
      })
      .catch(res => {
        const response = createErrorFromResponse(res);
        if (typeof response.errors !== 'string') {
          setServerErrors(response.errors, setError);
        } else {
          showModal({
            type: 'error',
            content: response.message,
          });
        }
      });
  };

  const isInternal = typeField && typeField.value === 'internal';
  const hasSelectedCat = category && category.value;
  const hasDefaultCat = defaultCat && defaultCat.value;

  // Templates field is visible only if
  // document is internal AND there is selected category
  const showTemplatesField = isInternal && (hasSelectedCat || hasDefaultCat);
  const templatesOptions = showTemplatesField ? templates[hasSelectedCat ? category.value : defaultCat.value] : [];

  const [hasSelectedActive, setHasSelectedActive] = useState(false);
  const hasInactiveEmployees = useMemo(() => employees.some(emp => emp.status !== 'active'), [employees]);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleExcludeActive = useCallback(() => {
    if (hasSelectedActive) {
      return;
    }

    const excludedEmpIds = employeesRef.current?.filter(emp => emp.status !== 'active').map(emp => emp.value as number);
    setExcludedEmployees(() => excludedEmpIds);
    setHasSelectedActive(() => true);
  }, [hasSelectedActive]);

  useEffect(() => {
    if (groupField.current?.length) {
      setHasSelectedActive(() => false);
    }
  }, [groupField]);

  useEffect(() => {
    const handleResize = () => {
      if (massButtonRowRef.current && isMassDoc) {
        const prevRow = massButtonRowRef.current?.previousElementSibling;
        if (prevRow?.classList.contains('row')) {
          const height = prevRow.clientHeight;
          massButtonRowRef.current.style.setProperty('--prev-row-height', `${height}px`);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMassDoc]);

  // Filter excluded employees if current employees don't include excluded ones
  useEffect(() => {
    if (!groupsFieldValue?.length || !groupField.current?.length || !employees.length) {
      return;
    }

    const employeeValues = employees.map(emp => emp.value as number);
    const shouldFilterExcluded = excludedEmployees.some(id => !employeeValues.includes(id));

    if (shouldFilterExcluded) {
      setExcludedEmployees(prev => {
        return prev.filter(id => employeeValues.includes(id));
      });
    }
  }, [employees, excludedEmployees, groupsFieldValue]);

  return (
    <>
      <Wizard {...buildWizardProps(typeField.value === 'external', currentStep, 1, totalSteps)} />
      <Row className="mt-4">
        <Col md={{ offset: 2, span: 8 }}>
          <Form
            methods={methods}
            onSubmit={onSubmit}
            btnLabel={t('common.saveAndContinue')}
            isHorizontal
            hideCancelBtn
            areBtnsCentered
            data-testid="form-component"
          >
            <WizardNavAndSubmit
              currentStep={currentStep}
              backStep={0}
              isSubmitting={isSubmitting}
            />
            {hasCanCreateMassDocPermission && (
              <FormGroup
                controlId="formGroupMassDoc"
                label={t('docWizard.step1.massDoc')}
                checkType="switch"
                name="is_multi_user"
                isHorizontal
                labelGrid={3}
              />
            )}
            {availableTypes && availableTypes.length > 1 && (
              <FormGroup
                controlId="formGroupType"
                label={t('docWizard.step1.docType')}
                type="select"
                as="select"
                name="type"
                isRequired
                isSearchable
                isHorizontal
                labelGrid={3}
                controlGrid={7}
                onChange={e => {
                  if (e.value === 'external') {
                    unregister('template_id');
                    clearErrors('template_id');
                  } else {
                    setValue('template_id', null);
                  }
                }}
                selectOptions={availableTypes.map((option: ISelectOptionProps) => ({
                  ...option,
                  label: types ? option.label : t(option.label),
                }))}
              />
            )}
            <FormGroup
              controlId="formGroupCategory"
              label={t('docWizard.step1.docCategory')}
              type="select"
              as="select"
              name="category_id"
              isRequired
              isClearable
              isSearchable
              isHorizontal
              labelGrid={3}
              controlGrid={7}
              onChange={() => setValue('template_id', null)}
              selectOptions={categories.map((cat: ISelectOptionProps) => ({
                ...cat,
                label: `${cat.value}. ${cat.label}`,
              }))}
            />
            {showTemplatesField && (
              <FormGroup
                controlId="formGroupDocTemplate"
                label={t('docWizard.step1.docTemplate')}
                type="select"
                as="select"
                name="template_id"
                isRequired={isInternal}
                isClearable
                isSearchable
                isHorizontal
                labelGrid={3}
                controlGrid={7}
                // When mass doc is selected, template options should be filtered
                selectOptions={
                  templatesOptions
                    ? templatesOptions.map((opt: ITemplateSelectOptionProps) => ({
                        // .filter((opt: ITemplateSelectOptionProps) => (isMassDoc ? opt.bulk_create : true))
                        ...opt,
                        label_en: null,
                        label: language === 'en' && opt.label_en ? opt.label_en : opt.label,
                      }))
                    : []
                }
              />
            )}
            {hasCanSelectEmployeePermission && !isMassDoc && (
              <FormGroup
                controlId="formGroupUser"
                label={t('docWizard.step1.docEmployee')}
                type="select"
                as="select"
                name="user_id"
                isRequired
                isClearable
                isSearchable
                isHorizontal
                labelGrid={3}
                controlGrid={7}
                hasOptionTooltip
                selectOptions={employees.map((emp: IUserSelectOptionProps) => ({
                  ...emp,
                  label: generateLabel(emp),
                  label_en: generateLabelEn(emp),
                }))}
                disableFilterOption
                limitOptions
              />
            )}
            {isMassDoc && (
              <>
                <FormGroup
                  controlId="formGroupGroups"
                  label={t('docWizard.step1.docGroup')}
                  type="select"
                  as="select"
                  name="groups"
                  isRequired
                  isMulti
                  isClearable
                  isSearchable
                  allowSelectAll
                  isHorizontal
                  labelGrid={3}
                  controlGrid={7}
                  selectOptions={groups}
                />
                <FormGroup
                  controlId="formGroupUsers"
                  label={t('docWizard.step1.docEmployee')}
                  type="select"
                  as="select"
                  name="users"
                  isRequired
                  isMulti
                  isSearchable={false}
                  isClearable={false}
                  isDisabled={isMassDoc}
                  showTotal
                  isHorizontal
                  labelGrid={3}
                  controlGrid={hasInactiveEmployees && groupField.current?.length && !hasSelectedActive ? 4 : 7}
                  selectOptions={employees}
                  limitOptions
                />
                <RowTuck ref={massButtonRowRef}>
                  <Row>
                    <Col lg={{ offset: 7, span: 3 }}>
                      <FormMassActionsRow>
                        <Button
                          style={{
                            opacity:
                              hasInactiveEmployees && groupField.current?.length && !hasSelectedActive ? '1' : '0',
                            pointerEvents:
                              hasInactiveEmployees && groupField.current?.length && !hasSelectedActive ? 'all' : 'none',
                          }}
                          btnType="primary"
                          onClick={handleExcludeActive}
                        >
                          {t('docWizard.step1.selectOnlyActive')}
                        </Button>
                      </FormMassActionsRow>
                    </Col>
                  </Row>
                </RowTuck>
              </>
            )}

            {isMassDoc && employees && !!groupField.current?.length && (
              <Row>
                <Col md={{ offset: 3, span: 7 }}>
                  <h6>{t('docWizard.step1.selected')}:</h6>
                  {employees.map((user: IUserSelectOptionProps) => {
                    return (
                      <div
                        className="d-flex align-items-center mb-2"
                        key={`${user.value}-${user.label}-${user.selected}`}
                      >
                        <OverlayTrigger
                          key="bottom"
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-bottom">
                              {excludedEmployees.includes(Number(user.value))
                                ? t('docWizard.step1.select')
                                : t('docWizard.step1.deselect')}
                            </Tooltip>
                          }
                        >
                          <Badge
                            pill
                            variant={excludedEmployees.includes(Number(user.value)) ? 'danger' : 'primary'}
                            className="mr-2"
                            onClick={() => toggleExclusion(Number(user.value))}
                            style={{ cursor: 'pointer', padding: '8px', fontSize: '12px' }}
                          >
                            {user.label}
                            {/* {templateId && templateId.is_multilingual ? ` / ${user.label_en}` : null} */}
                            {excludedEmployees.includes(Number(user.value)) ? (
                              <FaTimes style={{ marginLeft: '10px' }} />
                            ) : (
                              <FaCheck style={{ marginLeft: '10px' }} />
                            )}
                          </Badge>
                        </OverlayTrigger>
                      </div>
                    );
                  })}
                </Col>
              </Row>
            )}
          </Form>
        </Col>
      </Row>
    </>
  );
};

const FormMassActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 0 0 1px;

  & > button {
    width: 100%;
    position: relative;
    z-index: 2;

    @media (max-width: 992px) {
      width: auto;
    }
  }
`;

const RowTuck = styled.div`
  margin-top: calc((var(--prev-row-height, 2.375rem) * -1) - 1rem);
  margin-bottom: 1rem;

  @media (max-width: 992px) {
    margin-top: 0;
  }
`;

export default StepOne;
